import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  MapPin, Clock, Home as HomeIcon, ChevronRight, ChevronLeft, MessageCircle, Check, X,
  BedDouble, Utensils, Plane, Car, Camera, FileCheck, ShieldCheck, Users, Calendar, Star,
} from "lucide-react";
import api from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import SEO from "@/components/SEO";
import WeatherWidget from "@/components/WeatherWidget";
import BookingDialog from "@/components/BookingDialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { hasFitGrid, lowestPP, activePlans } from "@/lib/fitPricing";

const overviewItems = [
  { key: "accommodation", label: "Accommodation", Icon: BedDouble },
  { key: "meals", label: "Meals", Icon: Utensils },
  { key: "flights", label: "Flights", Icon: Plane },
  { key: "transfers", label: "Transfers", Icon: Car },
  { key: "sightseeing", label: "Sightseeing", Icon: Camera },
  { key: "visa", label: "Visa Assistance", Icon: FileCheck },
  { key: "insurance", label: "Travel Insurance", Icon: ShieldCheck },
];
const OV_FLAG = { accommodation: "hotel", meals: "meals", flights: "flight", transfers: "transfers", sightseeing: "sightseeing", visa: "visa", insurance: "insurance" };

export default function TourDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const settings = useSettings();
  const [tour, setTour] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [bookOpen, setBookOpen] = useState(false);
  const carouselRef = useRef(null);
  const scrollCarousel = (dir) => { const el = carouselRef.current; if (el) el.scrollBy({ left: dir * el.clientWidth * 0.9, behavior: "smooth" }); };
  const [others, setOthers] = useState([]);
  const [reviews, setReviews] = useState({ reviews: [], average: 0, count: 0 });

  useEffect(() => {
    api.get("/tours").then((r) => setOthers(r.data || [])).catch(() => setOthers([]));
  }, []);
  useEffect(() => {
    if (slug) api.get(`/tours/${slug}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setTour(null); setNotFound(false);
    api.get(`/tours/${slug}`).then((r) => { setTour(r.data); setActiveImg(0); }).catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    const g = tour?.gallery?.length ? tour.gallery : (tour?.hero_image ? [tour.hero_image] : []);
    if (g.length <= 1) return;
    const t = setInterval(() => setActiveImg((p) => (p + 1) % g.length), 3500);
    return () => clearInterval(t);
  }, [tour]);

  const waHref = () => {
    const msg = encodeURIComponent(`Hi Happy Holidays Wale, I'm interested in the "${tour?.title}" package. Please share details.`);
    return `https://wa.me/${settings?.whatsapp || "919876543210"}?text=${msg}`;
  };

  if (notFound) return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 text-center">
      <h1 className="font-display text-4xl font-bold text-navy">Tour not found</h1>
      <Link to="/tours" className="btn-gradient mt-6 rounded-full px-6 py-3 font-bold">Browse all tours</Link>
    </div>
  );
  if (!tour) return <div className="mx-auto max-w-7xl px-4 py-10"><div className="h-[420px] w-full skeleton rounded-3xl" /></div>;

  const gallery = tour.gallery?.length ? tour.gallery : [tour.hero_image];
  const cities = (tour.cities || []).filter((c) => c.city);
  const weatherCity = cities[0]?.city || tour.destination;
  const weatherRegion = cities[0]?.area || tour.region || "";
  const morePackages = others.filter((t) => t.slug !== tour.slug).slice(0, 12);
  const startPrice = Number(hasFitGrid(tour) ? lowestPP(tour) : (tour.price_adult || tour.price)) || 0;
  const hasPrice = tour.price_enabled !== false && startPrice > 0;
  const fromPrice = (t) => Number(hasFitGrid(t) ? lowestPP(t) : (t.price_adult || t.price) || 0);
  const schema = {
    "@context": "https://schema.org", "@type": "Product", name: tour.title,
    image: tour.og_image || tour.hero_image, description: tour.meta_description || tour.short_description,
    offers: { "@type": "Offer", price: tour.price, priceCurrency: "INR", availability: "https://schema.org/InStock" },
  };
  const faqSchema = tour.faqs?.length ? {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: tour.faqs.map((f) => ({ "@type": "Question", name: f.question, acceptedAnswer: { "@type": "Answer", text: f.answer } })),
  } : null;

  return (
    <>
      <SEO title={tour.seo_title || tour.title} description={tour.meta_description || tour.short_description} image={tour.og_image || tour.hero_image} type="product" schema={faqSchema ? [schema, faqSchema] : schema} />
      <BookingDialog open={bookOpen} onOpenChange={setBookOpen} tour={tour} />

      {/* Breadcrumb */}
      <div className="mx-auto max-w-7xl px-4 pt-6 md:px-8">
        <nav className="flex items-center gap-2 text-sm text-gray-500">
          <Link to="/" className="flex items-center gap-1 hover:text-brandorange"><HomeIcon className="h-4 w-4" /> Home</Link>
          <ChevronRight className="h-4 w-4" /><Link to="/tours" className="hover:text-brandorange">Tours</Link>
          <ChevronRight className="h-4 w-4" /><span className="text-navy">{tour.title}</span>
        </nav>
      </div>

      {/* HERO GALLERY */}
      <section className="mx-auto max-w-7xl px-4 py-6 md:px-8">
        <div className="grid gap-3 lg:grid-cols-[2fr_1fr]">
          <div className="group relative overflow-hidden rounded-3xl">
            <img src={gallery[activeImg]} alt={tour.title} className="aspect-[16/10] w-full object-cover" />
            <div className="absolute left-4 top-4 flex flex-wrap gap-2">
              <span className="rounded-full bg-white/90 px-3 py-1 text-xs font-bold text-navy">{tour.package_type === "GROUP" ? "Group Departure" : "FIT / Individual"}</span>
              {(tour.badges || []).map((b) => <span key={b} className="brand-gradient rounded-full px-3 py-1 text-xs font-bold text-white">{b}</span>)}
            </div>
            {gallery.length > 1 && (
              <>
                <button onClick={() => setActiveImg((activeImg - 1 + gallery.length) % gallery.length)} data-testid="hero-prev" aria-label="Previous image" className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-navy shadow-md transition hover:bg-white"><ChevronLeft className="h-5 w-5" /></button>
                <button onClick={() => setActiveImg((activeImg + 1) % gallery.length)} data-testid="hero-next" aria-label="Next image" className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-navy shadow-md transition hover:bg-white"><ChevronRight className="h-5 w-5" /></button>
                <span className="absolute bottom-3 right-3 rounded-full bg-black/50 px-2.5 py-1 text-xs font-bold text-white">{activeImg + 1} / {gallery.length}</span>
              </>
            )}
          </div>
          <div className="flex gap-3 overflow-x-auto pb-1 lg:grid lg:min-h-0 lg:grid-cols-2 lg:content-start lg:overflow-x-hidden lg:overflow-y-auto lg:pr-1 [scrollbar-width:thin]">
            {gallery.map((img, i) => (
              <button key={i} onClick={() => setActiveImg(i)} data-testid={`gallery-thumb-${i}`} className={`shrink-0 basis-[30%] overflow-hidden rounded-2xl transition-all lg:w-full lg:basis-auto ${activeImg === i ? "ring-2 ring-brandorange ring-offset-2" : ""}`}>
                <img src={img} alt="" className="aspect-square w-full object-cover transition-transform duration-500 hover:scale-105" />
              </button>
            ))}
          </div>
        </div>

        {/* Title row */}
        <div className="mt-8 flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
          <div>
            <div className="mb-2 flex flex-wrap items-center gap-4 text-sm font-medium text-gray-500">
              <span className="flex items-center gap-1"><MapPin className="h-4 w-4 text-brandorange" /> {tour.destination}</span>
              <span className="flex items-center gap-1"><Clock className="h-4 w-4 text-brandorange" /> {tour.duration}</span>
              <span className="flex items-center gap-1" data-testid="tour-rating"><Star className="h-4 w-4 fill-brandorange text-brandorange" /> {reviews.count > 0 ? `${reviews.average} (${reviews.count} review${reviews.count > 1 ? "s" : ""})` : "New"}</span>
            </div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">{tour.title}</h1>
          </div>
        </div>
      </section>

      {/* MAIN GRID */}
      <section className="mx-auto max-w-7xl px-4 pb-24 md:px-8">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="min-w-0">
            <p className="text-lg leading-relaxed text-gray-600">{tour.description}</p>

            {/* OVERVIEW ICON CARDS */}
            <h2 className="mt-10 font-display text-2xl font-bold text-navy">Tour Overview</h2>
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {overviewItems.filter((o) => tour.overview?.[o.key] && (tour.includes_flags?.[OV_FLAG[o.key]] ?? true)).map(({ key, label, Icon }) => (
                <div key={key} className="rounded-2xl border border-gray-100 bg-white p-4 card-soft">
                  <Icon className="mb-2 h-6 w-6 text-brandorange" />
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">{label}</p>
                  <p className="mt-0.5 text-sm font-semibold text-navy">{tour.overview[key]}</p>
                </div>
              ))}
            </div>

            {cities.length > 0 && (
              <div className="mt-6" data-testid="cities-covered">
                <h3 className="mb-3 flex items-center gap-2 font-display text-xl font-bold text-navy"><MapPin className="h-5 w-5 text-brandorange" /> Cover Cities</h3>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {cities.map((c, i) => (
                    <div key={i} className="rounded-2xl border border-gray-100 bg-white p-3 card-soft" data-testid={`city-${i}`}>
                      <p className="text-sm font-bold text-navy">{c.city}</p>
                      {c.area && <p className="text-xs text-gray-500">{c.area}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(tour.includes_flags?.hotel ?? true) && !hasFitGrid(tour) && (tour.hotels?.length || tour.hotel_name) && (
              <div className="mt-6" data-testid="hotel-block">
                <h3 className="mb-3 flex items-center gap-2 font-display text-xl font-bold text-navy"><BedDouble className="h-5 w-5 text-brandorange" /> Hotel Details</h3>
                <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white card-soft">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-400"><tr><th className="p-3">Nights</th><th className="p-3">City</th><th className="p-3">Hotel Name</th><th className="p-3">Room Category</th><th className="p-3">Meal Plan</th></tr></thead>
                    <tbody>
                      {(tour.hotels?.length ? tour.hotels : [{ hotel_name: tour.hotel_name }]).map((h, i) => (
                        <tr key={i} data-testid={`hotel-view-${i}`} className="border-t border-gray-50">
                          <td className="p-3 text-navy">{h.nights || "—"}</td>
                          <td className="p-3 text-navy">{h.city || "—"}</td>
                          <td className="p-3 font-semibold text-navy">{h.hotel_name || "—"}</td>
                          <td className="p-3 text-navy">{h.room_category || "—"}</td>
                          <td className="p-3 text-navy">{h.meal_plan || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {(tour.includes_flags?.flight ?? true) && (tour.flights?.length || tour.flight_details) && (
              <div className="mt-6" data-testid="flight-block">
                <h3 className="mb-3 flex items-center gap-2 font-display text-xl font-bold text-navy"><Plane className="h-5 w-5 text-brandorange" /> Flight Details</h3>
                <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white card-soft">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-400"><tr><th className="p-3">Sector</th><th className="p-3">Airline</th><th className="p-3">Flight No</th><th className="p-3">Departure</th><th className="p-3">Arrival</th></tr></thead>
                    <tbody>
                      {(tour.flights?.length ? tour.flights : [{ sector: tour.flight_details }]).map((f, i) => (
                        <tr key={i} data-testid={`flight-view-${i}`} className="border-t border-gray-50">
                          <td className="p-3 font-semibold text-navy">{f.sector || "—"}</td>
                          <td className="p-3 text-navy">{f.airline || "—"}</td>
                          <td className="p-3 text-navy">{f.flight_no || "—"}</td>
                          <td className="p-3 text-navy">{f.departure || "—"}</td>
                          <td className="p-3 text-navy">{f.arrival || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {hasFitGrid(tour) && (
              <div className="mt-8 space-y-6" data-testid="tour-hotel-options">
                {tour.hotel_options.map((o, oi) => {
                  const plans = activePlans(o);
                  return (
                    <div key={o.id || oi} data-testid={`tour-hotel-option-${oi}`} className="rounded-2xl border border-gray-100 bg-white p-5 card-soft">
                      <h3 className="mb-3 flex items-center gap-2 font-display text-xl font-bold text-navy"><BedDouble className="h-5 w-5 text-brandorange" /> {o.name || `Hotel Option ${oi + 1}`}</h3>
                      {(o.segments || []).length > 0 && (
                        <div className="mb-4 overflow-x-auto rounded-xl border border-gray-100">
                          <table className="w-full text-sm">
                            <thead className="bg-navy text-left text-xs uppercase tracking-wide text-white"><tr><th className="p-3">Nights</th><th className="p-3">City</th><th className="p-3">Hotel</th><th className="p-3">Room Category</th></tr></thead>
                            <tbody>{o.segments.map((s, si) => (<tr key={si} className="border-t border-gray-50"><td className="p-3 text-navy">{s.nights || "—"}</td><td className="p-3 text-navy">{s.destination || "—"}</td><td className="p-3 font-semibold text-navy">{s.hotel_name || "—"}</td><td className="p-3 text-navy">{s.room_category || "—"}</td></tr>))}</tbody>
                          </table>
                        </div>
                      )}
                      {(o.pax_rows || []).length > 0 && (
                        <div className="overflow-x-auto rounded-xl border border-gray-100">
                          <table className="w-full text-sm">
                            <thead className="bg-navy text-left text-xs uppercase tracking-wide text-white"><tr><th className="p-3">PAX</th>{plans.map((p) => <th key={p} className="p-3">{p} (₹ PP)</th>)}<th className="p-3">Vehicle</th></tr></thead>
                            <tbody>{o.pax_rows.map((r, ri) => (<tr key={r.id || ri} className="border-t border-gray-50"><td className="p-3 font-bold text-navy">{r.pax}</td>{plans.map((p) => <td key={p} className="p-3 text-navy">{Number(r.prices?.[p] || 0) > 0 ? `₹${Number(r.prices[p]).toLocaleString("en-IN")}` : "—"}</td>)}<td className="p-3 text-navy">{r.vehicle || "—"}</td></tr>))}</tbody>
                          </table>
                        </div>
                      )}
                      {(o.cwb_price > 0 || o.cnb_price > 0) && (
                        <div className="mt-3 flex flex-wrap gap-4 text-sm text-gray-600">
                          {o.cwb_price > 0 && <span>Child With Bed: <b className="text-navy">₹{Number(o.cwb_price).toLocaleString("en-IN")}</b></span>}
                          {o.cnb_price > 0 && <span>Child No Bed: <b className="text-navy">₹{Number(o.cnb_price).toLocaleString("en-IN")}</b></span>}
                          <span className="text-gray-400">Kids 0–5 free</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {tour.itinerary?.length > 0 && (
              <>
                <h2 className="mt-12 font-display text-2xl font-bold text-navy">Day-by-Day Itinerary</h2>
                {/* Desktop timeline */}
                <div className="mt-6 hidden md:block">
                  <div className="relative border-l-2 border-dashed border-brandorange/40 pl-8">
                    {tour.itinerary.map((d) => (
                      <div key={d.day} className="relative mb-8 last:mb-0" data-testid={`itinerary-day-${d.day}`}>
                        <span className="absolute -left-[42px] flex h-8 w-8 items-center justify-center rounded-full brand-gradient text-sm font-bold text-white">{d.day}</span>
                        <div className="rounded-2xl border border-gray-100 bg-white p-5 card-soft">
                          <p className="text-xs font-bold uppercase tracking-wide text-brandorange">Day {d.day}</p>
                          <h3 className="mt-1 font-display text-lg font-bold text-navy">{d.title}</h3>
                          <p className="mt-1 text-sm text-gray-500">{d.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Mobile accordion */}
                <div className="mt-6 md:hidden">
                  <Accordion type="single" collapsible className="space-y-3">
                    {tour.itinerary.map((d) => (
                      <AccordionItem key={d.day} value={`day-${d.day}`} data-testid={`itinerary-accordion-${d.day}`} className="overflow-hidden rounded-2xl border border-gray-100 bg-white card-soft">
                        <AccordionTrigger className="px-4 py-4 hover:no-underline">
                          <span className="flex items-center gap-3 text-left">
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full brand-gradient text-sm font-bold text-white">{d.day}</span>
                            <span className="font-display font-bold text-navy">{d.title}</span>
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4 text-sm text-gray-500">{d.description}</AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              </>
            )}

            {/* INCLUSIONS / EXCLUSIONS */}
            <div className="mt-12 grid gap-6 sm:grid-cols-2">
              <div className="rounded-3xl border border-emerald-100 bg-emerald-50/50 p-6">
                <h3 className="mb-4 font-display text-xl font-bold text-navy">Package Includes</h3>
                <ul className="space-y-3">
                  {tour.inclusions?.map((inc, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-gray-600"><Check className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" /> {inc}</li>
                  ))}
                </ul>
              </div>
              <div className="rounded-3xl border border-red-100 bg-red-50/40 p-6">
                <h3 className="mb-4 font-display text-xl font-bold text-navy">Package Excludes</h3>
                <ul className="space-y-3">
                  {tour.exclusions?.map((exc, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-gray-600"><X className="mt-0.5 h-5 w-5 shrink-0 text-brandred" /> {exc}</li>
                  ))}
                </ul>
              </div>
            </div>

            {/* BOOKING CONDITIONS (moved below includes/excludes, no collapse) */}
            {(tour.payment_policy || tour.cancellation_policy || tour.terms_policy) && (
              <div className="mt-12" data-testid="booking-conditions">
                <h2 className="font-display text-2xl font-bold text-navy">Booking Conditions</h2>
                <div className="mt-5 space-y-3">
                  {tour.payment_policy && <div className="rounded-2xl border border-gray-100 bg-white p-5 card-soft"><p className="font-display font-semibold text-navy">Payment Condition</p><p className="mt-2 whitespace-pre-line text-sm text-gray-500">{tour.payment_policy}</p></div>}
                  {tour.cancellation_policy && <div className="rounded-2xl border border-gray-100 bg-white p-5 card-soft"><p className="font-display font-semibold text-navy">Cancellation Condition</p><p className="mt-2 whitespace-pre-line text-sm text-gray-500">{tour.cancellation_policy}</p></div>}
                  {tour.terms_policy && <div className="rounded-2xl border border-gray-100 bg-white p-5 card-soft"><p className="font-display font-semibold text-navy">Terms & Conditions</p><p className="mt-2 whitespace-pre-line text-sm text-gray-500">{tour.terms_policy}</p></div>}
                </div>
              </div>
            )}

            {/* MORE PACKAGES CAROUSEL */}
            {morePackages.length > 0 && (
              <div className="mt-12" data-testid="more-packages">
                <div className="flex items-center justify-between">
                  <h2 className="font-display text-2xl font-bold text-navy">More Packages You'll Love</h2>
                  <div className="flex gap-2">
                    <button onClick={() => scrollCarousel(-1)} data-testid="carousel-prev" aria-label="Previous" className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 text-navy transition-colors hover:border-brandorange hover:text-brandorange"><ChevronLeft className="h-5 w-5" /></button>
                    <button onClick={() => scrollCarousel(1)} data-testid="carousel-next" aria-label="Next" className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 text-navy transition-colors hover:border-brandorange hover:text-brandorange"><ChevronRight className="h-5 w-5" /></button>
                  </div>
                </div>
                <div ref={carouselRef} className="mt-5 flex snap-x gap-6 overflow-x-auto pb-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  {morePackages.map((t) => (
                    <Link key={t.slug} to={`/tour/${t.slug}`} data-testid={`more-pkg-${t.slug}`} className="group shrink-0 basis-[calc(50%-0.75rem)] snap-start overflow-hidden rounded-2xl border border-gray-100 bg-white card-soft transition-transform hover:-translate-y-1 sm:basis-[calc(30%-1rem)]">
                      <div className="relative h-36 overflow-hidden"><img src={t.hero_image} alt={t.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /><span className="absolute left-2 top-2 rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-bold text-navy">{t.destination}</span></div>
                      <div className="p-3">
                        <p className="line-clamp-2 min-h-[2.5rem] text-sm font-bold text-navy">{t.title}</p>
                        <div className="mt-2 flex items-center justify-between"><span className="text-xs text-gray-400">{t.duration}</span><span className="font-display text-sm font-extrabold text-brandorange">{fromPrice(t) > 0 ? `₹${fromPrice(t).toLocaleString("en-IN")}` : "Enquire"}</span></div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* CONVERSION CTA */}
            <div className="mt-12 rounded-3xl bg-navy p-8 text-center" style={{ backgroundColor: "#002244" }}>
              <h2 className="font-display text-3xl font-bold text-white">Ready to start your journey?</h2>
              <p className="mx-auto mt-3 max-w-lg text-white/70">Tell us where you want to go and our travel experts will take care of the rest.</p>
              <div className="mt-6 flex flex-wrap justify-center gap-4">
                <button onClick={() => setBookOpen(true)} data-testid="cta-enquire-btn" className="btn-gradient rounded-full px-7 py-3.5 font-bold">Enquire Now</button>
                <a href={waHref()} target="_blank" rel="noopener noreferrer" data-testid="cta-whatsapp-btn" className="flex items-center gap-2 rounded-full bg-whatsapp px-7 py-3.5 font-bold text-white" style={{ backgroundColor: "#25D366" }}><MessageCircle className="h-5 w-5" /> WhatsApp Us</a>
              </div>
            </div>
            {reviews.reviews.length > 0 && (
              <div className="mt-12" data-testid="tour-reviews">
                <h2 className="font-display text-2xl font-bold text-navy">Guest Reviews</h2>
                <div className="mt-2 flex items-center gap-2 text-sm text-gray-500"><Star className="h-5 w-5 fill-brandorange text-brandorange" /><span className="font-bold text-navy">{reviews.average}</span> · {reviews.count} review{reviews.count > 1 ? "s" : ""}</div>
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  {reviews.reviews.map((r) => (
                    <div key={r.id} className="rounded-2xl border border-gray-100 bg-white p-5 card-soft">
                      <div className="flex gap-0.5">{[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`h-4 w-4 ${n <= r.rating ? "fill-brandorange text-brandorange" : "text-gray-200"}`} />)}</div>
                      <p className="mt-2 text-sm text-gray-600">{r.comment}</p>
                      <p className="mt-2 text-xs font-bold text-navy">— {r.user_name || "Guest"}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {tour.faqs?.length > 0 && (
              <>
                <h2 className="mt-12 font-display text-2xl font-bold text-navy">Frequently Asked Questions</h2>
                <div className="mt-5 space-y-3">
                  {tour.faqs.map((f, i) => (
                    <div key={i} data-testid={`faq-${i}`} className="rounded-2xl border border-gray-100 bg-white p-5 card-soft">
                      <p className="font-display font-semibold text-navy">{f.question}</p>
                      <p className="mt-2 whitespace-pre-line text-sm text-gray-500">{f.answer}</p>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* CONVERSION CTA */}
          </div>

          {/* STICKY BOOKING CARD (desktop) */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 space-y-4">
              <div className="rounded-3xl border border-gray-100 bg-white p-6 card-soft">
                <p className="text-sm text-gray-400">Starting from</p>
                {hasPrice ? (
                  <div className="flex items-end gap-2"><span className="font-display text-4xl font-extrabold text-navy">₹{startPrice.toLocaleString("en-IN")}</span><span className="mb-1 text-sm text-gray-400">/ adult</span></div>
                ) : (
                  <div className="font-display text-3xl font-extrabold text-brandorange" data-testid="price-on-request">On Request</div>
                )}

                {tour.package_type === "GROUP" && tour.departures?.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="flex items-center gap-2 text-sm font-bold text-navy"><Calendar className="h-4 w-4 text-brandorange" /> Fixed Departures</p>
                    {tour.departures.map((d) => {
                      const left = d.seats_total - d.seats_booked;
                      return (
                        <div key={d.id} className="flex items-center justify-between rounded-xl bg-navy/5 px-3 py-2 text-sm">
                          <span className="font-semibold text-navy">{new Date(d.date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>
                          <span className={`flex items-center gap-1 text-xs font-bold ${left <= 5 ? "text-brandred" : "text-emerald-600"}`}><Users className="h-3.5 w-3.5" /> {left > 0 ? `${left} left` : "Sold Out"}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {hasPrice && <button onClick={() => nav(`/book/${tour.slug}`)} data-testid="sticky-book-btn" className="btn-gradient mt-5 w-full rounded-full py-3.5 font-bold">Book Now</button>}
                <button onClick={() => setBookOpen(true)} data-testid="sticky-enquire-btn" className="mt-3 w-full rounded-full border-2 border-navy py-3 font-bold text-navy transition-colors hover:bg-navy hover:text-white">Enquire Now</button>
                <a href={waHref()} target="_blank" rel="noopener noreferrer" data-testid="sticky-whatsapp-btn" className="mt-3 flex w-full items-center justify-center gap-2 rounded-full py-3 font-bold text-white" style={{ backgroundColor: "#25D366" }}><MessageCircle className="h-5 w-5" /> WhatsApp Us</a>
              </div>

              <WeatherWidget lat={tour.lat} lon={tour.lon} city={weatherCity} region={weatherRegion} />
            </div>
          </aside>
        </div>

        {/* Weather on mobile */}
        <div className="mt-8 lg:hidden"><WeatherWidget lat={tour.lat} lon={tour.lon} city={weatherCity} region={weatherRegion} /></div>
      </section>

      {/* STICKY MOBILE CTA */}
      <div className="fixed bottom-0 left-0 z-30 w-full border-t border-gray-100 bg-white/90 p-3 backdrop-blur-xl lg:hidden">
        <div className="flex items-center gap-3">
          <div className="flex-1"><p className="text-[11px] text-gray-400">From</p><p className="font-display text-lg font-extrabold text-navy">{hasPrice ? `₹${startPrice.toLocaleString("en-IN")}` : "On Request"}</p></div>
          <a href={waHref()} target="_blank" rel="noopener noreferrer" data-testid="mobile-whatsapp-btn" className="flex h-12 w-12 items-center justify-center rounded-full text-white" style={{ backgroundColor: "#25D366" }}><MessageCircle className="h-6 w-6" /></a>
          {hasPrice ? (
            <button onClick={() => nav(`/book/${tour.slug}`)} data-testid="mobile-book-btn" className="btn-gradient flex-1 rounded-full py-3 font-bold">Book Now</button>
          ) : (
            <button onClick={() => setBookOpen(true)} data-testid="mobile-enquire-btn" className="btn-gradient flex-1 rounded-full py-3 font-bold">Enquire Now</button>
          )}
        </div>
      </div>
    </>
  );
}
